import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import Alert from '../components/ui/Alert/Alert';

import { RootState } from '../ducks/store';
import { updateAuthUserProps } from '../ducks/auth';
import { getActiveDeselectedCourses } from '../utils/reports';
import { useUpdateUserReportDeselectCoursesMutation } from '../services/users';
import { setActiveDeselectedCourses as setActive } from '../ducks/features/teamsSlice';
import { hasTeamManagerRole, ToastifyNoDefaultStyling } from '../utils';

interface IUseActiveDeselectedCourses {
  activeDeselectedCourses: string[];
  setActiveDeselectedCourses: (courses: string[]) => void;
  storeDeselectedCourses: (teamIds: string[]) => void;
}

export const useActiveDeselectedCourses = (): IUseActiveDeselectedCourses => {
  // ===========================================================================
  // Redux
  // ===========================================================================

  const dispatch = useDispatch();

  const { userId, isTeamManager, activeDeselectedCourses } = useSelector((state: RootState) => {
    const user = state.auth.authenticatedUser;
    const isTeamManager = hasTeamManagerRole(user);
    const activeDeselectedCourses = getActiveDeselectedCourses(user, isTeamManager);
    const userId = user?._id;

    return {
      userId,
      isTeamManager,
      activeDeselectedCourses,
    };
  });

  // ===========================================================================
  // RTK Query
  // ===========================================================================

  const [updateUserReportDeselectCourses] = useUpdateUserReportDeselectCoursesMutation();

  // ===========================================================================
  // Functions
  // ===========================================================================

  const setActiveDeselectedCourses = (courses: string[]) => dispatch(setActive(courses));

  const storeDeselectedCourses = async (reportDeselectedCourses: string[]) => {
    // Team Manager: Store to DB
    if (isTeamManager && userId) {
      try {
        await updateUserReportDeselectCourses({ userId, reportDeselectedCourses });
        dispatch(updateAuthUserProps({ reportDeselectedCourses }));
      } catch (e) {
        toast.error(
          <Alert
            icon="error"
            heading="Failed to save courses. Please try again."
            variant="danger"
          />,
          ToastifyNoDefaultStyling,
        );
      }

      return;
    }
  };

  return {
    activeDeselectedCourses,
    setActiveDeselectedCourses,
    storeDeselectedCourses,
  };
};
